import React, {useState} from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import {DateTimePicker} from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import {NextSyncOffset, SyncTargetCreate} from '../types';
import {createSyncTarget, uploadManualSyncTarget} from '../api/dataApi';

export type SnackFn = (args: { open: boolean; message: string; severity: 'success' | 'error' }) => void;

interface CreateSyncTargetDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => Promise<void> | void;
  showSnack: SnackFn;
}

export default function CreateSyncTargetDialog(props: CreateSyncTargetDialogProps) {
  const { open, onClose, onCreated, showSnack } = props;
  const [form, setForm] = useState<SyncTargetCreate>({ source: 'Manual', name: '', description: '', nextSync: undefined, nextSyncOffset: '1M' });
  const [manualUploading, setManualUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Create sync target</DialogTitle>
      <Box component="form" onSubmit={async (e) => {
        e.preventDefault();
        if (form.source === 'Manual') {
          return;
        }
        try {
          setSubmitting(true);
          const payload: SyncTargetCreate = {
            source: form.source,
            name: form.name.trim(),
            description: form.description?.trim() ? form.description.trim() : undefined,
            nextSync: form.nextSync ? new Date(form.nextSync).toISOString() : undefined,
            nextSyncOffset: form.nextSyncOffset,
          };
          if (!payload.source || !payload.name) {
            showSnack({ open: true, message: 'Source and Name are required', severity: 'error' });
            return;
          }
          await createSyncTarget(payload);
          showSnack({ open: true, message: 'Sync target created', severity: 'success' });
          setForm({ source: 'Manual', name: '', description: '', nextSync: undefined, nextSyncOffset: '1M' });
          onClose();
          await onCreated();
        } catch (err) {
          showSnack({ open: true, message: 'Failed to create sync target', severity: 'error' });
        } finally {
          setSubmitting(false);
        }
      }}>
        <DialogContent dividers>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel id="source-label">Source</InputLabel>
                <Select
                  labelId="source-label"
                  label="Source"
                  value={form.source}
                  onChange={(e) => setForm((f) => ({ ...f, source: e.target.value as any }))}
                >
                  <MenuItem value="Manual">Manual</MenuItem>
                  <MenuItem value="Eurostat">Eurostat</MenuItem>
                  <MenuItem value="ECB">ECB</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {form.source !== 'Manual' && (
              <>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth>
                    <InputLabel id="offset-label">Next sync offset</InputLabel>
                    <Select
                      labelId="offset-label"
                      label="Next sync offset"
                      value={form.nextSyncOffset || '1M'}
                      onChange={(e) => setForm((f) => ({ ...f, nextSyncOffset: e.target.value as NextSyncOffset }))}
                    >
                      <MenuItem value="1D">1D</MenuItem>
                      <MenuItem value="1W">1W</MenuItem>
                      <MenuItem value="1M">1M</MenuItem>
                      <MenuItem value="1Y">1Y</MenuItem>
                      <MenuItem value="14D">14D</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <DateTimePicker
                    label="Next sync"
                    value={form.nextSync ? dayjs(form.nextSync) : null}
                    onChange={(val) => setForm((f) => ({ ...f, nextSync: val ? dayjs(val).toISOString() : undefined }))}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Name"
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    placeholder="identifier or name"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Description"
                    value={form.description || ''}
                    onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                    placeholder="Optional description"
                    fullWidth
                    multiline
                    minRows={2}
                  />
                </Grid>
              </>
            )}

            {form.source === 'Manual' && (
              <Grid item xs={12}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
                  <Typography variant="body1">Upload .xlsx file to create a Manual sync target</Typography>
                  <input
                    type="file"
                    accept=".xlsx"
                    style={{ display: 'none' }}
                    id="manual-upload-input"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      try {
                        setManualUploading(true);
                        const created = await uploadManualSyncTarget(file);
                        const names = created.map(c => c.name).filter(Boolean).join(', ');
                        showSnack({ open: true, message: `Uploaded: ${names}` , severity: 'success' });
                        onClose();
                        setForm({ source: 'Manual', name: '', description: '', nextSync: undefined, nextSyncOffset: '1M' });
                        await onCreated();
                      } catch (err) {
                        showSnack({ open: true, message: 'Upload failed', severity: 'error' });
                      } finally {
                        setManualUploading(false);
                        const input = document.getElementById('manual-upload-input') as HTMLInputElement | null;
                        if (input) input.value = '';
                      }
                    }}
                  />
                  <label htmlFor="manual-upload-input">
                    <Button component="span" variant="contained" disabled={manualUploading}>
                      {manualUploading ? 'Uploading...' : 'Upload .xlsx'}
                    </Button>
                  </label>
                </Stack>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={manualUploading}>Cancel</Button>
          {form.source !== 'Manual' && (
            <Button type="submit" variant="contained" disabled={submitting}>Create</Button>
          )}
        </DialogActions>
      </Box>
    </Dialog>
  );
}
